/**
 * GenerateFuecForm Component
 * Formulario para generar un nuevo FUEC
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Form,
  Select,
  DatePicker,
  Row,
  Col,
  Divider,
  Alert,
  Spin,
  Card,
  Tag,
  Space,
  Input,
  Checkbox,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  CarOutlined,
  UserOutlined,
  BankOutlined,
  FileTextOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import PropTypes from "prop-types";

import {
  generateFuec,
  fetchClientes,
  fetchVehiculos,
  fetchConductores,
  fetchObjetosContratoList,
  fetchDepartamentos,
  fetchCiudadesByDepartamento,
  calcularVigenciaFuec,
} from "./api";
import { filtrarOpcion } from "../../../utils/filtroSelect";

const { Option } = Select;
const { Text } = Typography;

// Texto corto para el valor seleccionado; el desplegable muestra el objeto completo
const resumen = (texto = "", max = 80) => (texto.length > max ? `${texto.substring(0, max)}...` : texto);

// Responsable contratante: por defecto los datos del cliente (los que imprime el FUEC si no se editan)
const CAMPOS_RESPONSABLE = ["responsable_nombre", "responsable_identificacion", "responsable_telefono", "responsable_direccion"];
const responsableDelCliente = (cliente) => ({
  responsable_nombre: cliente?.nombre_cliente || "",
  responsable_identificacion: cliente?.nit || "",
  responsable_telefono: cliente?.telefono || "",
  responsable_direccion: cliente?.direccion || "",
});

const GenerateFuecForm = ({ visible, onClose, onSuccess, trasladoId }) => {
  const [form] = Form.useForm();
  const responsableDiferente = Form.useWatch("responsable_diferente", form);
  const objetoSeleccionadoId = Form.useWatch("objeto_contrato_id", form);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Datos para selects
  const [clientes, setClientes] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [conductores, setConductores] = useState([]);
  const [objetosContrato, setObjetosContrato] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [ciudadesOrigen, setCiudadesOrigen] = useState([]);
  const [ciudadesDestino, setCiudadesDestino] = useState([]);

  // Estado seleccionado
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [selectedVehiculo, setSelectedVehiculo] = useState(null);
  const [selectedConductor, setSelectedConductor] = useState(null);

  // Estado para vigencia calculada automáticamente
  const [vigenciaCalculada, setVigenciaCalculada] = useState(null);
  const [calculandoVigencia, setCalculandoVigencia] = useState(false);

  // Cargar datos iniciales
  const loadInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const [
        clientesRes,
        vehiculosRes,
        conductoresRes,
        objetosRes,
        deptosRes,
      ] = await Promise.all([
        fetchClientes(),
        fetchVehiculos(),
        fetchConductores(),
        fetchObjetosContratoList(),
        fetchDepartamentos(),
      ]);

      setClientes(Array.isArray(clientesRes) ? clientesRes : clientesRes.data || []);
      setVehiculos(Array.isArray(vehiculosRes) ? vehiculosRes : vehiculosRes.data || []);
      setConductores(Array.isArray(conductoresRes) ? conductoresRes : conductoresRes.data || []);
      setObjetosContrato(Array.isArray(objetosRes) ? objetosRes : objetosRes.data || []);
      setDepartamentos(Array.isArray(deptosRes) ? deptosRes : deptosRes.data || []);
    } catch (error) {
      console.error("Error cargando datos:", error);
      message.error("Error al cargar datos del formulario");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (visible) {
      loadInitialData();
      form.resetFields();
      setSelectedCliente(null);
      setSelectedVehiculo(null);
      setSelectedConductor(null);
      setCiudadesOrigen([]);
      setCiudadesDestino([]);
      setVigenciaCalculada(null);

      // Establecer fecha de inicio por defecto (hoy)
      form.setFieldsValue({
        fecha_inicio_vigencia: dayjs(),
      });
    }
  }, [visible, form, loadInitialData]);

  // Calcular vigencia automática cuando se selecciona vehículo y conductor
  const calcularVigenciaAutomatica = useCallback(async (vehiculoId, conductorId, fechaInicio) => {
    if (!vehiculoId || !conductorId) {
      setVigenciaCalculada(null);
      return;
    }

    setCalculandoVigencia(true);
    try {
      const resultado = await calcularVigenciaFuec(
        vehiculoId,
        conductorId,
        fechaInicio ? fechaInicio.format("YYYY-MM-DD") : null
      );
      setVigenciaCalculada(resultado);
    } catch (error) {
      console.error("Error calculando vigencia:", error);
      message.warning("No se pudo calcular la vigencia automática");
      setVigenciaCalculada(null);
    } finally {
      setCalculandoVigencia(false);
    }
  }, []);

  // Handlers de seleccion
  const handleClienteChange = useCallback(
    (clienteId) => {
      const cliente = clientes.find((c) => c.id === clienteId);
      setSelectedCliente(cliente);
      form.setFieldsValue({ responsable_diferente: false, ...responsableDelCliente(cliente) });

      // Pre-llenar objeto de contrato si el cliente tiene uno por defecto
      if (cliente?.objeto_contrato_id) {
        form.setFieldsValue({ objeto_contrato_id: cliente.objeto_contrato_id });
      }
    },
    [clientes, form]
  );

  const handleVehiculoChange = useCallback(
    (vehiculoId) => {
      const vehiculo = vehiculos.find((v) => v.id === vehiculoId);
      setSelectedVehiculo(vehiculo);

      // Pre-seleccionar conductor asignado al vehiculo
      let conductorId = form.getFieldValue("conductor_id");
      if (vehiculo?.conductor_id) {
        form.setFieldsValue({ conductor_id: vehiculo.conductor_id });
        conductorId = vehiculo.conductor_id;
        const conductor = conductores.find((c) => c.id === vehiculo.conductor_id);
        setSelectedConductor(conductor);
      }

      // Calcular vigencia automática si hay vehículo y conductor
      const fechaInicio = form.getFieldValue("fecha_inicio_vigencia");
      if (vehiculoId && conductorId && fechaInicio) {
        calcularVigenciaAutomatica(vehiculoId, conductorId, fechaInicio);
      }
    },
    [vehiculos, conductores, form, calcularVigenciaAutomatica]
  );

  const handleConductorChange = useCallback(
    (conductorId) => {
      const conductor = conductores.find((c) => c.id === conductorId);
      setSelectedConductor(conductor);

      // Calcular vigencia automática si hay vehículo y conductor
      const vehiculoId = form.getFieldValue("vehiculo_id");
      const fechaInicio = form.getFieldValue("fecha_inicio_vigencia");
      if (vehiculoId && conductorId && fechaInicio) {
        calcularVigenciaAutomatica(vehiculoId, conductorId, fechaInicio);
      }
    },
    [conductores, form, calcularVigenciaAutomatica]
  );

  const handleDepartamentoOrigenChange = useCallback(async (deptoId) => {
    form.setFieldsValue({ origen_ciudad_id: undefined });
    setCiudadesOrigen([]);

    if (deptoId) {
      try {
        const ciudades = await fetchCiudadesByDepartamento(deptoId);
        setCiudadesOrigen(Array.isArray(ciudades) ? ciudades : ciudades.data || []);
      } catch (error) {
        console.error("Error cargando ciudades origen:", error);
      }
    }
  }, [form]);

  const handleDepartamentoDestinoChange = useCallback(async (deptoId) => {
    form.setFieldsValue({ destino_ciudad_id: undefined });
    setCiudadesDestino([]);

    if (deptoId) {
      try {
        const ciudades = await fetchCiudadesByDepartamento(deptoId);
        setCiudadesDestino(Array.isArray(ciudades) ? ciudades : ciudades.data || []);
      } catch (error) {
        console.error("Error cargando ciudades destino:", error);
      }
    }
  }, [form]);

  // Al desmarcar "responsable diferente" vuelven los datos del cliente
  const handleResponsableDiferenteChange = useCallback(
    (e) => {
      if (!e.target.checked) {
        form.setFieldsValue(responsableDelCliente(selectedCliente));
        form.setFields(CAMPOS_RESPONSABLE.map((name) => ({ name, errors: [] })));
      }
    },
    [form, selectedCliente]
  );

  // Handler para cambio de fecha de inicio
  const handleFechaInicioChange = useCallback(
    (fecha) => {
      const vehiculoId = form.getFieldValue("vehiculo_id");
      const conductorId = form.getFieldValue("conductor_id");
      if (vehiculoId && conductorId && fecha) {
        calcularVigenciaAutomatica(vehiculoId, conductorId, fecha);
      }
    },
    [form, calcularVigenciaAutomatica]
  );

  // Submit
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      // La fecha fin se calcula automáticamente en el backend
      // basada en la fecha más próxima de vencimiento de documentos
      const payload = {
        cliente_id: values.cliente_id,
        vehiculo_id: values.vehiculo_id,
        conductor_id: values.conductor_id,
        objeto_contrato_id: values.objeto_contrato_id,
        origen_ciudad_id: values.origen_ciudad_id,
        origen_departamento_id: values.origen_departamento_id,
        destino_ciudad_id: values.destino_ciudad_id,
        destino_departamento_id: values.destino_departamento_id,
        fecha_inicio_vigencia: values.fecha_inicio_vigencia.format("YYYY-MM-DD"),
        // fecha_fin_vigencia se calcula automáticamente en el backend
        traslado_id: trasladoId || null,
      };
      if (values.responsable_diferente) {
        payload.responsable_diferente = true;
        CAMPOS_RESPONSABLE.forEach((campo) => {
          payload[campo] = (values[campo] || "").trim();
        });
      }

      await generateFuec(payload);
      onSuccess();
    } catch (error) {
      if (error.errorFields) {
        message.error("Por favor complete todos los campos requeridos");
      } else {
        console.error("Error generando FUEC:", error);
        const erroresCampos = error.response?.data?.errors;
        if (error.response?.status === 422 && erroresCampos && typeof erroresCampos === "object") {
          const camposForm = Object.keys(form.getFieldsValue(true));
          const conocidos = Object.keys(erroresCampos).filter((name) => camposForm.includes(name));
          form.setFields(conocidos.map((name) => ({ name, errors: [].concat(erroresCampos[name]) })));
          message.error([].concat(Object.values(erroresCampos)[0])[0] || "Revise los datos del formulario");
        } else {
          const errorMsg =
            error.response?.data?.error || "Error al generar el FUEC";
          message.error(errorMsg);
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Búsqueda por el texto visible (incluye opciones con Tag/iconos, sin tildes)
  const filterOption = filtrarOpcion;

  return (
    <Modal
      title={
        <Space>
          <FileTextOutlined />
          <span>Generar FUEC</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      onOk={handleSubmit}
      okText="Generar FUEC"
      cancelText="Cancelar"
      confirmLoading={submitting}
      width={900}
      destroyOnClose
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          requiredMark="optional"
          scrollToFirstError
        >
          <Alert
            message="Informacion importante"
            description="El FUEC se generara con los datos actuales del vehiculo y conductor. Verifique que la informacion este actualizada."
            type="info"
            showIcon
            icon={<InfoCircleOutlined />}
            style={{ marginBottom: 16 }}
          />

          {/* Seccion Cliente y Contrato */}
          <Divider orientation="left">
            <BankOutlined /> Cliente y Contrato
          </Divider>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="cliente_id"
                label="Cliente / Contratante"
                rules={[{ required: true, message: "Seleccione un cliente" }]}
              >
                <Select
                  placeholder="Seleccione cliente/EPS"
                  showSearch
                  filterOption={filterOption}
                  onChange={handleClienteChange}
                >
                  {clientes.map((c) => (
                    <Option key={c.id} value={c.id}>
                      {c.nombre_cliente} {c.nit ? `- NIT: ${c.nit}` : ""}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="objeto_contrato_id"
                label="Objeto del Contrato"
                rules={[
                  { required: true, message: "Seleccione objeto del contrato" },
                ]}
              >
                <Select
                  placeholder="Seleccione objeto del contrato"
                  showSearch
                  filterOption={filterOption}
                  optionLabelProp="label"
                  popupMatchSelectWidth={false}
                  dropdownStyle={{ maxWidth: "min(720px, 95vw)" }}
                >
                  {objetosContrato.map((o) => (
                    <Option key={o.id} value={o.id} label={resumen(o.objeto_contrato || "")}>
                      <Tooltip title={o.objeto_contrato} placement="right" mouseEnterDelay={0.4}>
                        <div style={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.4, padding: "2px 0" }}>
                          {o.objeto_contrato || ""}
                        </div>
                      </Tooltip>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              {objetoSeleccionadoId && (
                <Text type="secondary" style={{ display: "block", marginTop: -16, marginBottom: 16, fontSize: 12, whiteSpace: "normal" }}>
                  {objetosContrato.find((o) => o.id === objetoSeleccionadoId)?.objeto_contrato}
                </Text>
              )}
            </Col>
          </Row>

          {/* Info del cliente seleccionado */}
          {selectedCliente && (
            <Card size="small" style={{ marginBottom: 16, background: "#f5f5f5" }}>
              <Row gutter={16}>
                <Col span={6}>
                  <strong>NIT:</strong> {selectedCliente.nit || "Sin registrar"}
                </Col>
                <Col span={6}>
                  <strong>Teléfono:</strong> {selectedCliente.telefono || "N/A"}
                </Col>
                <Col span={6}>
                  <strong>Contrato:</strong>{" "}
                  {selectedCliente.numero_contrato_fuec || "Sin asignar"}
                </Col>
                <Col span={6}>
                  <strong>Versión:</strong> {selectedCliente.fuec_version || 1}
                </Col>
              </Row>
            </Card>
          )}

          {/* Responsable contratante: datos del cliente, editables si es otra persona */}
          {selectedCliente && (
            <Card
              size="small"
              title={<span><UserOutlined /> Responsable contratante</span>}
              extra={
                <Form.Item name="responsable_diferente" valuePropName="checked" noStyle initialValue={false}>
                  <Checkbox onChange={handleResponsableDiferenteChange}>
                    Responsable contratante diferente al cliente
                  </Checkbox>
                </Form.Item>
              }
              style={{ marginBottom: 16 }}
            >
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="responsable_nombre"
                    label="Nombre"
                    rules={[{ required: !!responsableDiferente, whitespace: true, message: "Ingrese el nombre del responsable" }]}
                  >
                    <Input maxLength={255} disabled={!responsableDiferente} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="responsable_identificacion"
                    label="Identificación"
                    rules={[{ required: !!responsableDiferente, whitespace: true, message: "Ingrese la identificación del responsable" }]}
                  >
                    <Input maxLength={30} disabled={!responsableDiferente} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="responsable_telefono" label="Teléfono">
                    <Input maxLength={30} disabled={!responsableDiferente} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="responsable_direccion" label="Dirección">
                    <Input maxLength={200} disabled={!responsableDiferente} />
                  </Form.Item>
                </Col>
              </Row>
              {!responsableDiferente && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  El FUEC usará los datos del cliente. Marque la casilla si el responsable es otra persona.
                </Text>
              )}
            </Card>
          )}

          {/* Seccion Vehiculo */}
          <Divider orientation="left">
            <CarOutlined /> Vehiculo
          </Divider>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="vehiculo_id"
                label="Vehiculo"
                rules={[{ required: true, message: "Seleccione un vehiculo" }]}
              >
                <Select
                  placeholder="Seleccione vehiculo"
                  showSearch
                  filterOption={filterOption}
                  onChange={handleVehiculoChange}
                >
                  {vehiculos.map((v) => (
                    <Option key={v.id} value={v.id}>
                      <Tag color="blue">{v.placa}</Tag> {v.marca} {v.modelo} -{" "}
                      Cod: {v.codigo || "S/N"}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="conductor_id"
                label="Conductor"
                rules={[{ required: true, message: "Seleccione un conductor" }]}
              >
                <Select
                  placeholder="Seleccione conductor"
                  showSearch
                  filterOption={filterOption}
                  onChange={handleConductorChange}
                >
                  {conductores.map((c) => (
                    <Option key={c.id} value={c.id}>
                      <UserOutlined /> {c.nombre} - CC {c.numero_documento}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Info del vehiculo y conductor seleccionado */}
          {(selectedVehiculo || selectedConductor) && (
            <Card size="small" style={{ marginBottom: 16, background: "#f5f5f5" }}>
              <Row gutter={16}>
                {selectedVehiculo && (
                  <>
                    <Col span={8}>
                      <strong>Placa:</strong>{" "}
                      <Tag color="blue">{selectedVehiculo.placa}</Tag>
                    </Col>
                    <Col span={8}>
                      <strong>Marca/Modelo:</strong> {selectedVehiculo.marca}{" "}
                      {selectedVehiculo.modelo}
                    </Col>
                    <Col span={8}>
                      <strong>Tarjeta Op.:</strong>{" "}
                      {selectedVehiculo.numero_tarjeta_operacion || "N/A"}
                    </Col>
                  </>
                )}
              </Row>
              {selectedConductor && (
                <Row gutter={16} style={{ marginTop: 8 }}>
                  <Col span={8}>
                    <strong>Documento:</strong> {selectedConductor.numero_documento}
                  </Col>
                  <Col span={8}>
                    <strong>Licencia:</strong>{" "}
                    {selectedConductor.licencia_conduccion || "N/A"}
                  </Col>
                  <Col span={8}>
                    <strong>Vig. Licencia:</strong>{" "}
                    {selectedConductor.fecha_vencimiento_licencia
                      ? dayjs(selectedConductor.fecha_vencimiento_licencia).format(
                          "DD/MM/YYYY"
                        )
                      : "N/A"}
                  </Col>
                </Row>
              )}
            </Card>
          )}

          {/* Seccion Origen y Destino */}
          <Divider orientation="left">
            <EnvironmentOutlined /> Origen y Destino
          </Divider>

          <Row gutter={16}>
            <Col xs={24} md={6}>
              <Form.Item
                name="origen_departamento_id"
                label="Departamento Origen"
                rules={[{ required: true, message: "Requerido" }]}
              >
                <Select
                  placeholder="Departamento"
                  showSearch
                  filterOption={filterOption}
                  onChange={handleDepartamentoOrigenChange}
                >
                  {departamentos.map((d) => (
                    <Option key={d.id} value={d.id}>
                      {d.nombre}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item
                name="origen_ciudad_id"
                label="Ciudad Origen"
                rules={[{ required: true, message: "Requerido" }]}
              >
                <Select
                  placeholder="Ciudad"
                  showSearch
                  filterOption={filterOption}
                  disabled={ciudadesOrigen.length === 0}
                >
                  {ciudadesOrigen.map((c) => (
                    <Option key={c.id} value={c.id}>
                      {c.nombre}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item
                name="destino_departamento_id"
                label="Departamento Destino"
                rules={[{ required: true, message: "Requerido" }]}
              >
                <Select
                  placeholder="Departamento"
                  showSearch
                  filterOption={filterOption}
                  onChange={handleDepartamentoDestinoChange}
                >
                  {departamentos.map((d) => (
                    <Option key={d.id} value={d.id}>
                      {d.nombre}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item
                name="destino_ciudad_id"
                label="Ciudad Destino"
                rules={[{ required: true, message: "Requerido" }]}
              >
                <Select
                  placeholder="Ciudad"
                  showSearch
                  filterOption={filterOption}
                  disabled={ciudadesDestino.length === 0}
                >
                  {ciudadesDestino.map((c) => (
                    <Option key={c.id} value={c.id}>
                      {c.nombre}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Seccion Vigencia */}
          <Divider orientation="left">
            <CalendarOutlined /> Vigencia del FUEC
          </Divider>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                name="fecha_inicio_vigencia"
                label="Fecha de Inicio"
                rules={[
                  { required: true, message: "Seleccione la fecha de inicio" },
                ]}
              >
                <DatePicker
                  format="DD/MM/YYYY"
                  style={{ width: "100%" }}
                  placeholder="Fecha inicio"
                  disabledDate={(current) =>
                    current && current < dayjs().startOf("day")
                  }
                  onChange={handleFechaInicioChange}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={16}>
              {calculandoVigencia ? (
                <Alert
                  message="Calculando vigencia..."
                  description="Analizando fechas de vencimiento de documentos del vehículo y conductor."
                  type="info"
                  showIcon
                  icon={<Spin size="small" />}
                  style={{ marginTop: 30 }}
                />
              ) : vigenciaCalculada ? (
                <Card size="small" style={{ marginTop: 22, background: "#f6ffed", borderColor: "#b7eb8f" }}>
                  <Row gutter={16}>
                    <Col span={8}>
                      <strong>Fecha Fin:</strong>{" "}
                      <Tag color="green">{dayjs(vigenciaCalculada.fecha_fin).format("DD/MM/YYYY")}</Tag>
                    </Col>
                    <Col span={8}>
                      <strong>Días de vigencia:</strong>{" "}
                      <Tag color="blue">{vigenciaCalculada.dias_vigencia} días</Tag>
                    </Col>
                    <Col span={8}>
                      <strong>Limitado por:</strong>{" "}
                      {vigenciaCalculada.documento_limitante ? (
                        <Tag color="orange">{vigenciaCalculada.documento_limitante.tipo}</Tag>
                      ) : (
                        <Tag>Configuración por defecto</Tag>
                      )}
                    </Col>
                  </Row>
                  {vigenciaCalculada.documento_limitante && (
                    <div style={{ marginTop: 8, fontSize: 12, color: "#666" }}>
                      <InfoCircleOutlined /> {vigenciaCalculada.mensaje}
                    </div>
                  )}
                </Card>
              ) : (
                <Alert
                  message="Vigencia automática"
                  description="Seleccione vehículo y conductor para calcular la fecha de vigencia automáticamente basada en los vencimientos de documentos."
                  type="info"
                  showIcon
                  style={{ marginTop: 30 }}
                />
              )}
            </Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
};

GenerateFuecForm.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  trasladoId: PropTypes.number,
};

GenerateFuecForm.defaultProps = {
  trasladoId: null,
};

export default GenerateFuecForm;
